import { useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import useOfflineCentresStore from '@stores/offline-centres';
import { Spacings, mediaQuery } from '@styles/index';
import { segment } from '@actions/analytics';
import StyledTabs from '@base/Tabs/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@base/Button/Button';
import CheckBig from '@icon/CheckBig';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Check from '@icon/Check';
import CountUp from 'react-countup';
import { businessPlatform } from '@utils/constants';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { getTotalAmtBySubscription } from '@utils/choosePlanHelper';
import { commas, currencySymbolMap } from '@utils/textHelper';
import {
  subscriptionSubTypes,
  subscriptionSubTypesObj,
  subscriptionTypesObj,
  subscriptionTypes
} from '@constants/index';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import {
  CHECKOUT_SUBSCRIPTION_TYPE_SELECTED,
  CHECKOUT_DURATION_SELECTED
} from '@cont/Checkout/constants/segment';

const Wrapper = styled.div`
  flex: 1;
  ${mediaQuery.aboveTablet} {
    max-width: 658px;
    margin-bottom: ${Spacings.SPACING_21B};
  }
`;

const Heading = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    max-width: 75%;
  }
`;

const TabContainer = styled(StyledTabs)`
  margin-bottom: ${Spacings.SPACING_8B};
  .MuiTabs-indicator {
    border-radius: 5px 5px 0 0;
  }
  .MuiTab-root {
    min-width: max-content;
    margin-right: ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    .MuiTabs-scroller {
      overflow: scroll !important;
      scrollbar-width: none;
      ::-webkit-scrollbar {
        display: none;
      }
    }
  }
`;

const StyledTab = styled(Tab)`
  ${mediaQuery.uptoMobile} {
    &.MuiTab-root {
      max-width: 200px;
      min-width: min-content;
    }
  }
  &.Mui-selected {
    color: var(--color-text-primary);
  }
`;

const PlanCardWrapper = styled.div`
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_5B};
  flex-shrink: 0;
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_3B};
  }
  border: 1.5px solid
    ${({ $isSelected }) =>
      $isSelected ? 'var(--color-i-black)' : 'var(--color-i-divider)'};
  background-color: var(--color-i-white);
`;

const RadioCheckBig = styled(CheckBig)`
  flex-shrink: 0;
  margin-top: 6px;
`;

const RadioBtn = styled.div`
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  border-radius: 50%;
  border: 2px solid var(--color-divider);
  margin-top: 6px;
  flex-shrink: 0;
`;

const PlanCardContent = styled.div`
  flex: 1;
`;

const PlanCardTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PlanCardTitle = styled(H4)``;

const EffectivePrice = styled(H4)`
  flex-shrink: 0;
`;

const MrpPrice = styled(P2)`
  color: var(--color-i-gray-13);
  text-decoration: line-through;
  flex: 1;
  flex-shrink: 0;
  text-align: right;
  margin-top: 6px;
`;

const PlanCardDescription = styled(P1)`
  color: var(--color-i-gray-13);
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const BottomCard = styled.div`
  position: fixed;
  bottom: 0;
  padding: ${Spacings.SPACING_4B};
  box-shadow: 0px -8px 32px var(--color-i-gray-v2);
  max-width: inherit;
  width: 100%;
  background: var(--color-i-white);
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    left: 0;
  }
`;

const BottomCardHeading = styled(H5)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const CityCardDescription = styled(P1)`
  color: var(--color-i-gray-13);
`;

const DescriptionListWrapper = styled.div`
  max-width: 80%;
`;

const DescriptionListItemWrapper = styled.div`
  display: flex;
  margin-top: 6px;
  gap: ${Spacings.SPACING_1B};
`;

const DescriptionItemText = styled(P1)`
  color: ${({ $disabled }) =>
    $disabled ? 'var(--color-i-gray-13)' : 'var(--color-i-green)'};
`;

const PlanItemDescriptionList = ({ metaData }) => {
  if (!metaData) return null;
  const { offerings } = metaData.offline_centre;
  return (
    <DescriptionListWrapper>
      {offerings?.map((offering, index) => (
        <DescriptionListItemWrapper key={String(index)}>
          <Check
            size="24px"
            color={
              index === offerings.length - 1
                ? 'var(--color-i-gray-13)'
                : 'var(--color-i-green)'
            }
          />
          <DescriptionItemText $disabled={index === offerings.length - 1}>
            {offering}
          </DescriptionItemText>
        </DescriptionListItemWrapper>
      ))}
    </DescriptionListWrapper>
  );
};

const PlanCard = ({
  isReferralSubmitted,
  subscription,
  isSelected,
  setSelectedPlan
}) => {
  const totalAmount = getTotalAmtBySubscription(subscription);
  const { title } = subscription;
  const currency = currencySymbolMap(subscription.currency);

  return (
    <PlanCardWrapper
      $isSelected={isSelected}
      onClick={() => setSelectedPlan(subscription)}
    >
      {isSelected ? (
        <RadioCheckBig size="24px" color="var(--color-i-black)" />
      ) : (
        <RadioBtn />
      )}
      <PlanCardContent>
        <PlanCardTitleWrapper>
          <PlanCardTitle>{title} </PlanCardTitle>
          <EffectivePrice>
            <CountUp
              start={subscription.price}
              end={totalAmount}
              separator=","
              prefix={currency}
              duration={1}
            />
          </EffectivePrice>
        </PlanCardTitleWrapper>
        <PlanCardDescription>
          <PlanItemDescriptionList metaData={subscription.meta_data} />
          {isReferralSubmitted && (
            <MrpPrice>
              {`${currency}${commas(Math.round(subscription.price))}`}
            </MrpPrice>
          )}
        </PlanCardDescription>
      </PlanCardContent>
    </PlanCardWrapper>
  );
};

const PlanSelection = ({ selectedCity, offlineCentrePlans }) => {
  const { query, push } = useRouter();
  let defaultTab = 0;
  let defaultPlanIndex = 0;
  if (query.goal) {
    const tabIndex = offlineCentrePlans.findIndex(
      (item) => item.goal.uid === query.goal
    );
    if (tabIndex > 0) defaultTab = tabIndex;
  }
  if (query.uid) {
    const planIndex = offlineCentrePlans[defaultTab].subscriptions.findIndex(
      (subscription) => subscription.uid === query.uid
    );
    if (planIndex > 0) defaultPlanIndex = planIndex;
  }

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [selectedPlan, setSelectedPlan] = useState(
    offlineCentrePlans[selectedTab].subscriptions[defaultPlanIndex]
  );

  const selectedGoalUid = offlineCentrePlans[selectedTab].goal.uid;
  const subType = subscriptionSubTypesObj[subscriptionSubTypes.OFFLINE];
  const type = subscriptionTypesObj[subscriptionTypes.CENTRE];

  const eventProps = useCheckoutEventProps(selectedGoalUid);

  useFirstRenderSkip(() => {
    setSelectedPlan(offlineCentrePlans[selectedTab].subscriptions[0]);
  }, [selectedTab]);

  const handleTabChange = (e, newTabValue) => {
    setSelectedTab(newTabValue);
  };

  const handlePlanSelection = (subscription) => {
    setSelectedPlan(subscription);
    segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
      ...eventProps,
      business_platform: businessPlatform.UA_OFFLINE,
      subscription_sub_type: subType,
      // Adding new prop here for upgrade flow
      subscription_type: type,
      batch_group_type: subType,
      batch_group_name: subscription.title,
      selected_city: selectedCity?.name,
      goal_name: offlineCentrePlans[selectedTab].goal.name,
      batch_title: subscription.title
    });
  };

  const handleProceedToPay = () => {
    const selectedGoal = offlineCentrePlans[selectedTab].goal;
    const selectedSubscription = offlineCentrePlans[
      selectedTab
    ].subscriptions.find(
      (subscription) => subscription.uid === selectedPlan.uid
    );
    useOfflineCentresStore.setState({
      ...useOfflineCentresStore.getState(),
      pricingData: offlineCentrePlans[selectedTab],
      subscriptionUID: selectedSubscription.uid,
      cityUID: selectedCity.uid
    });

    push({
      pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      query: {
        goalSlug: '-',
        goalUID: selectedGoal.uid,
        subscriptionUID: selectedSubscription.uid
      }
    });
    segment.track(CHECKOUT_DURATION_SELECTED, {
      ...eventProps,
      business_platform: businessPlatform.UA_OFFLINE,
      subscription_sub_type: subType,
      batch_group_type: subType,
      // Adding new prop here for upgrade flow
      subscription_type: type,
      batch_group_name: selectedPlan?.title,
      selected_city: selectedCity?.name,
      goal_name: offlineCentrePlans[selectedTab].goal.name,
      batch_title: selectedPlan?.title
    });
  };

  return (
    <Wrapper>
      <Heading>Select your programme to proceed</Heading>
      <TabContainer
        value={selectedTab}
        $selectedTabColor="var(--color-base-fill)"
        onChange={handleTabChange}
      >
        {offlineCentrePlans.map((offlineCentrePlan, index) => (
          <StyledTab
            label={offlineCentrePlan.goal.name}
            key={offlineCentrePlan.goal.uid}
            value={index}
          />
        ))}
      </TabContainer>
      {offlineCentrePlans[selectedTab].subscriptions.map(
        (selectedGoalOption) => (
          <PlanCard
            key={selectedGoalOption.uid}
            subscription={selectedGoalOption}
            isSelected={selectedPlan.uid === selectedGoalOption.uid}
            setSelectedPlan={handlePlanSelection}
          />
        )
      )}

      <BottomCard>
        <div>
          <BottomCardHeading>{selectedPlan.title}</BottomCardHeading>
          <CityCardDescription>{selectedCity?.name}</CityCardDescription>
        </div>
        <Button
          label="Proceed to pay"
          color="black"
          onClick={() => handleProceedToPay(selectedCity)}
        />
      </BottomCard>
    </Wrapper>
  );
};

export default PlanSelection;
